import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  User,
  Calendar,
  AlertTriangle,
  Loader2,
  RefreshCcw,
  History,
  UserMinus,
  CalendarClock,
  Trash2,
  Tag,
  DoorOpen,
  Search,
  Check,
  Users,
  Unlink,
} from 'lucide-react';
import { ScheduleEntry, ScheduleSession, scheduleService, TimingChangePreview } from '../../services/scheduleService';
import { ApiError } from '@/utils/api';
import type { StaffUser } from '@/services/userService';
import type { SessionType } from '../../services/calendarSettingsService';
import type { RoomFlat } from '@/services/campusInfrastructureService';

interface EditSessionModalProps {
  entry: ScheduleEntry;
  sessionTypes: SessionType[];
  professors: StaffUser[];
  rooms?: RoomFlat[];
  academicYearId: string;
  preselectedSessionDate?: string;
  onClose: () => void;
  onUpdate: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

type EditMode = 'view' | 'reschedule' | 'change_timing' | 'change_professor' | 'change_session_type' | 'change_room' | 'delete' | 'unshare';

interface ProfessorHistory {
  professorId: string;
  professorName: string;
  changedAt: string;
  changedBy: string;
}

export const EditSessionModal: React.FC<EditSessionModalProps> = ({
  entry,
  sessionTypes,
  professors,
  rooms = [],
  academicYearId: _academicYearId,
  preselectedSessionDate,
  onClose,
  onUpdate,
  onError,
  onSuccess,
}) => {
  const [mode, setMode] = useState<EditMode>('view');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cancel form state
  const [cancelReason, setCancelReason] = useState('');

  // Reschedule form state
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleStartTime, setRescheduleStartTime] = useState(entry.startTime);
  const [rescheduleEndTime, setRescheduleEndTime] = useState(entry.endTime);

  // Change timing form state (for rest of semester)
  const [newDayOfWeek, setNewDayOfWeek] = useState(entry.dayOfWeek);
  const [newStartTime, setNewStartTime] = useState(entry.startTime);
  const [newEndTime, setNewEndTime] = useState(entry.endTime);
  type TimingChangeScope = 'single' | 'all_following';
  const [timingChangeScope, setTimingChangeScope] = useState<TimingChangeScope>('all_following');
  const [timingPreview, setTimingPreview] = useState<TimingChangePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Change professor form state
  const [newProfessorId, setNewProfessorId] = useState(entry.professorId);
  const [_professorHistory, _setProfessorHistory] = useState<ProfessorHistory[]>([]);

  // Change session type form state
  const [newSessionTypeId, setNewSessionTypeId] = useState(entry.sessionTypeId);

  // Change room form state
  const [newRoomId, setNewRoomId] = useState<string>(entry.roomId || '');
  const [roomSearchQuery, setRoomSearchQuery] = useState('');
  type RoomChangeScope = 'current' | 'all_following';
  const [roomChangeScope, setRoomChangeScope] = useState<RoomChangeScope>('all_following');

  // Delete state
  type DeleteScope = 'current' | 'all_following';
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('current');
  const [selectedDeleteSessionId, setSelectedDeleteSessionId] = useState<string | null>(null);
  // Server refusal: sessions in the deletion range already carry an attendance
  // list. `canForce` is true only for a DP/DG/BM, who must confirm explicitly.
  const [attendanceWarning, setAttendanceWarning] = useState<{
    canForce: boolean;
    message: string;
    sessions: { id: string; date: string; startTime: string; endTime: string }[];
  } | null>(null);

  // Session instances
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Load session instances
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const entryData = await scheduleService.getEntry(entry.id);
        if (entryData && 'sessions' in entryData) {
          const loadedSessions = (entryData as any).sessions || [];
          setSessions(loadedSessions);

          // Pre-select session based on the clicked date
          if (preselectedSessionDate && loadedSessions.length > 0) {
            const matchingSession = loadedSessions.find((s: ScheduleSession) => {
              const sessionDateStr = typeof s.date === 'string'
                ? s.date.split('T')[0]
                : new Date(s.date).toISOString().split('T')[0];
              return sessionDateStr === preselectedSessionDate && !s.isCancelled;
            });
            if (matchingSession) {
              setSelectedSessionId(matchingSession.id);
              setSelectedDeleteSessionId(matchingSession.id);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }
    };
    loadSessions();
  }, [entry.id, preselectedSessionDate]);

  // Calculate end time based on session type duration
  const updateEndTime = (startTime: string, sessionTypeId: string) => {
    const sessionType = sessionTypes.find(st => st.id === sessionTypeId);
    if (sessionType) {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const endMinutes = startHour * 60 + startMin + sessionType.durationMinutes;
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      return `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
    }
    return startTime;
  };

  // Handle reschedule
  const handleReschedule = async () => {
    if (!rescheduleDate) {
      onError('Veuillez sélectionner une nouvelle date');
      return;
    }
    if (!cancelReason.trim()) {
      onError('Veuillez indiquer une raison pour le report');
      return;
    }

    if (!selectedSessionId) {
      onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the reschedule endpoint that creates a new session under the same entry
      await scheduleService.rescheduleSession(
        selectedSessionId,
        rescheduleDate,
        rescheduleStartTime,
        rescheduleEndTime,
        cancelReason || 'Report de séance'
      );

      onSuccess('Séance reportée avec succès');
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors du report');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load preview when times or scope change
  const loadTimingPreview = async () => {
    if (!selectedSessionId) return;

    setIsLoadingPreview(true);
    try {
      const preview = await scheduleService.previewTimingChange(
        entry.id,
        newStartTime,
        newEndTime,
        selectedSessionId,
        timingChangeScope === 'single'
      );
      setTimingPreview(preview);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setTimingPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Load preview when entering change_timing mode or when params change
  useEffect(() => {
    if (mode === 'change_timing' && selectedSessionId) {
      loadTimingPreview();
    }
  }, [mode, selectedSessionId, newStartTime, newEndTime, timingChangeScope]);

  // Handle change timing (applies from session N onwards, preserves sessions before N)
  const handleChangeTiming = async () => {
    if (!selectedSessionId) {
      onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the dedicated endpoint that preserves sessions before N and regenerates from N onwards
      const result = await scheduleService.changeTiming(
        entry.id,
        newDayOfWeek,
        newStartTime,
        newEndTime,
        selectedSessionId,
        undefined, // ramadanStartTime
        undefined, // ramadanEndTime
        timingChangeScope === 'single'
      );

      // Build success message based on scope
      let successMessage = timingChangeScope === 'single'
        ? 'Horaire modifié pour cette séance uniquement'
        : 'Horaire modifié pour cette séance et les suivantes (historique préservé)';

      // Add adjustment info if available
      if (result.adjustmentsMade && result.adjustmentsMade.length > 0) {
        successMessage += '\n\nAjustements effectués:\n' + result.adjustmentsMade.map(a => `• ${a}`).join('\n');
      }

      // Include partial session notification if present
      if (result.partialSessionNotification) {
        const actualHours = result.partialSessionNotification.actualDurationMinutes / 60;
        const expectedHours = result.partialSessionNotification.expectedDurationMinutes / 60;
        successMessage += `\n\n⚠️ Note: La dernière séance (${result.partialSessionNotification.date}) sera de ${actualHours}h au lieu de ${expectedHours}h pour compléter le volume horaire.`;
      }

      // Vacations/holidays can shift regenerated weeks onto an occupied slot
      if (result.conflictWarnings && result.conflictWarnings.length > 0) {
        successMessage += '\n\n⚠️ Conflits à vérifier sur les dates régénérées:\n' + result.conflictWarnings.map(w => `• ${w}`).join('\n');
      }

      onSuccess(successMessage);
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle change professor (applies from session N onwards, preserves sessions before N)
  const handleChangeProfessor = async () => {
    if (newProfessorId === entry.professorId) {
      onError('Veuillez sélectionner un professeur différent');
      return;
    }

    if (!selectedSessionId) {
      onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the dedicated endpoint that preserves history for sessions before N
      await scheduleService.changeProfessor(entry.id, newProfessorId, selectedSessionId);
      onSuccess('Professeur modifié pour cette séance et les suivantes (historique préservé)');
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle change session type (applies from session N onwards, preserves sessions before N)
  const handleChangeSessionType = async () => {
    if (newSessionTypeId === entry.sessionTypeId) {
      onError('Veuillez sélectionner un type de séance différent');
      return;
    }

    if (!selectedSessionId) {
      onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the dedicated endpoint that preserves sessions before N and regenerates from N onwards
      const result = await scheduleService.changeSessionType(
        entry.id,
        newSessionTypeId,
        selectedSessionId
        // TODO: Add Ramadan time prompts if needed
      );

      // Build success message, include partial session notification if present
      let successMessage = 'Type de séance modifié pour cette séance et les suivantes (historique préservé)';
      if (result.partialSessionNotification) {
        const actualHours = result.partialSessionNotification.actualDurationMinutes / 60;
        const expectedHours = result.partialSessionNotification.expectedDurationMinutes / 60;
        successMessage += `\n\n⚠️ Note: La dernière séance (${result.partialSessionNotification.date}) sera de ${actualHours}h au lieu de ${expectedHours}h pour compléter le volume horaire.`;
      }

      onSuccess(successMessage);
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete entry
  /** Make this course independent again (its sessions stay in place) */
  const handleUnshare = async () => {
    setIsSubmitting(true);
    try {
      await scheduleService.unshareEntry(entry.id);
      onSuccess('Groupes séparés : ce cours est de nouveau indépendant');
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la séparation des groupes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEntry = async (force = false) => {
    const selected = sessions.find((x) => x.id === selectedDeleteSessionId);
    // "From this session onward" only needs a date: the clicked day works even
    // when no session instance matched it exactly.
    const canDeleteFrom = deleteScope === 'all_following' && (selected || preselectedSessionDate);
    if (!selected && !canDeleteFrom) {
      onError('Veuillez sélectionner une séance');
      return;
    }
    setIsSubmitting(true);
    try {
      if (deleteScope === 'current') {
        await scheduleService.deleteSession(selected!.id, force);
        onSuccess('Séance supprimée');
      } else {
        // This session and the following ones; earlier sessions (history,
        // attendance) are kept on purpose.
        const fromDate = String(selected?.date ?? preselectedSessionDate ?? '').slice(0, 10);
        const result = await scheduleService.deleteSessionsFrom(entry.id, fromDate, force);
        onSuccess(
          result.entryDeleted
            ? `${result.deleted} séance(s) supprimée(s), le cours n'a plus de séance`
            : `${result.deleted} séance(s) supprimée(s), ${result.remainingSessions} séance(s) passée(s) conservée(s)`,
        );
      }
      setAttendanceWarning(null);
      onUpdate();
      onClose();
    } catch (err) {
      const data = err instanceof ApiError ? err.data : null;
      if (data && data.code === 'SESSIONS_WITH_ATTENDANCE') {
        setAttendanceWarning({
          canForce: data.canForce === true,
          message: typeof data.message === 'string' ? data.message : (err as Error).message,
          sessions: Array.isArray(data.sessions) ? (data.sessions as { id: string; date: string; startTime: string; endTime: string }[]) : [],
        });
      } else {
        onError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle change room (applies from session N onwards, preserves sessions before N)
  const handleChangeRoom = async () => {
    // Check if room actually changed
    const currentRoomId = entry.roomId || '';
    if (newRoomId === currentRoomId) {
      onError('Veuillez sélectionner une salle différente');
      return;
    }

    setIsSubmitting(true);
    try {
      if (roomChangeScope === 'current') {
        // Update only the specific session's room
        if (!selectedSessionId) {
          onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
          setIsSubmitting(false);
          return;
        }
        await scheduleService.changeRoom(entry.id, newRoomId || null, selectedSessionId, 'current');
        onSuccess('Salle modifiée pour cette séance uniquement');
      } else {
        // Update from session N onwards, preserving sessions before N
        if (!selectedSessionId) {
          onError('Veuillez d\'abord cliquer sur une séance dans l\'emploi du temps');
          setIsSubmitting(false);
          return;
        }
        await scheduleService.changeRoom(
          entry.id,
          newRoomId || null,
          selectedSessionId,
          'all_following'
        );
        onSuccess('Salle modifiée pour cette séance et les suivantes (historique préservé)');
      }
      onUpdate();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}`;
  };

  // Calculate duration from start and end times
  const calculateDurationFromTimes = (startTime: string, endTime: string): number => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    return (endHour * 60 + endMin) - (startHour * 60 + startMin);
  };

  // Get the selected session (for displaying actual times during Ramadan)
  const getSelectedSession = (): ScheduleSession | null => {
    if (selectedSessionId) {
      return sessions.find(s => s.id === selectedSessionId) || null;
    }
    return null;
  };

  const dayLabels: Record<number, string> = {
    1: 'Lundi',
    2: 'Mardi',
    3: 'Mercredi',
    4: 'Jeudi',
    5: 'Vendredi',
    6: 'Samedi',
  };

  // Get the professor for the selected session - check for override first
  const getSessionProfessor = () => {
    const selectedSession = sessions.find(s => s.id === selectedSessionId);
    if (selectedSession?.overrideProfessor) {
      return selectedSession.overrideProfessor;
    }
    if (selectedSession?.professorIdOverride) {
      return professors.find(p => p.id === selectedSession.professorIdOverride);
    }
    return professors.find(p => p.id === entry.professorId);
  };
  const currentProfessor = getSessionProfessor();
  const newProfessor = professors.find(p => p.id === newProfessorId);
  const sessionType = sessionTypes.find(st => st.id === entry.sessionTypeId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${sessionType?.color || '#6366F1'}20` }}
            >
              <CalendarClock className="w-5 h-5" style={{ color: sessionType?.color || '#6366F1' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === 'view' ? 'Détails de la séance' :
                 mode === 'reschedule' ? 'Reporter la séance' :
                 mode === 'change_timing' ? 'Modifier l\'horaire' :
                 mode === 'change_professor' ? 'Changer le professeur' :
                 mode === 'change_session_type' ? 'Changer le type de séance' :
                 mode === 'change_room' ? 'Changer la salle' :
                 mode === 'delete' ? 'Supprimer la séance' :
                 mode === 'unshare' ? 'Séparer les groupes' :
                 'Détails de la séance'}
              </h3>
              <p className="text-sm text-gray-500">{entry.moduleElement?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {mode === 'view' && (
            <div className="space-y-6">
              {/* Current Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {dayLabels[entry.dayOfWeek]} {entry.isRecurring ? '(récurrent)' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {(() => {
                      const selectedSession = getSelectedSession();
                      const displayStart = selectedSession?.startTime || entry.startTime;
                      const displayEnd = selectedSession?.endTime || entry.endTime;
                      const actualDuration = calculateDurationFromTimes(displayStart, displayEnd);
                      return `${displayStart} - ${displayEnd} (${formatDuration(actualDuration)})`;
                    })()}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {currentProfessor
                      ? `${currentProfessor.firstName} ${currentProfessor.lastName}`
                      : 'Non assigné'
                    }
                  </span>
                </div>
                {/* Session Type */}
                <div className="flex items-center gap-3 text-sm">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{
                      backgroundColor: `${sessionType?.color || '#6366F1'}20`,
                      color: sessionType?.color || '#6366F1',
                    }}
                  >
                    {sessionType?.name || 'Type inconnu'}
                  </span>
                </div>
                {/* Room Info */}
                <div className="flex items-center gap-3 text-sm">
                  <DoorOpen className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {(() => {
                      const currentRoom = rooms.find(r => r.id === entry.roomId);
                      if (currentRoom) return `${currentRoom.name} (${currentRoom.floorName})`;
                      // Fall back to the room loaded with the entry — the campus
                      // rooms list may not contain it (filtered types, other campus)
                      return entry.room?.name || 'Salle non assignée';
                    })()}
                  </span>
                </div>
                {/* Shared session: the other groups taught together */}
                {entry.sharedWith && entry.sharedWith.length > 0 && (
                  <div className="flex items-start gap-3 text-sm">
                    <Users className="w-4 h-4 text-indigo-500 mt-0.5" />
                    <span className="text-indigo-700">
                      Séance partagée avec{' '}
                      {entry.sharedWith
                        .map((sw) => `${sw.groupeName} (${sw.moduleElementName}${sw.filiereName ? `, ${sw.filiereName}` : ''})`)
                        .join(', ')}
                      <span className="block text-xs text-indigo-500 mt-0.5">
                        Même professeur, même salle, mêmes dates. Les changements de salle, de professeur, les reports et
                        annulations s'appliquent à tous les groupes. Chaque groupe garde sa liste de présence.
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setMode('reschedule')}
                  className="flex flex-col items-start gap-1 px-4 py-3 border border-amber-200 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <RefreshCcw size={18} />
                    <span className="text-sm font-medium">Reporter cette séance</span>
                  </div>
                  <span className="text-xs text-amber-600 ml-6">Une seule séance</span>
                </button>
                <button
                  onClick={() => setMode('change_timing')}
                  className="flex flex-col items-start gap-1 px-4 py-3 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Clock size={18} />
                    <span className="text-sm font-medium">Modifier l'horaire</span>
                  </div>
                  <span className="text-xs text-blue-600 ml-6">Cette séance et les suivantes</span>
                </button>
                <button
                  onClick={() => setMode('change_professor')}
                  className="flex items-center gap-2 px-4 py-3 border border-purple-200 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <UserMinus size={18} />
                  <span className="text-sm font-medium">Changer professeur</span>
                </button>
                <button
                  onClick={() => setMode('change_session_type')}
                  className="flex items-center gap-2 px-4 py-3 border border-teal-200 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
                >
                  <Tag size={18} />
                  <span className="text-sm font-medium">Changer type</span>
                </button>
                {rooms.length > 0 && (
                  <button
                    onClick={() => setMode('change_room')}
                    className="flex items-center gap-2 px-4 py-3 border border-cyan-200 bg-cyan-50 text-cyan-700 rounded-lg hover:bg-cyan-100 transition-colors"
                  >
                    <DoorOpen size={18} />
                    <span className="text-sm font-medium">Changer salle</span>
                  </button>
                )}
                <button
                  onClick={() => setMode('delete')}
                  className="flex items-center gap-2 px-4 py-3 border border-gray-300 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Trash2 size={18} />
                  <span className="text-sm font-medium">Supprimer</span>
                </button>
                {entry.sharedWith && entry.sharedWith.length > 0 && (
                  <button
                    onClick={() => setMode('unshare')}
                    className="flex items-center gap-2 px-4 py-3 border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    <Unlink size={18} />
                    <span className="text-sm font-medium">Séparer les groupes</span>
                  </button>
                )}
              </div>

              {/* Professor History */}
              {_professorHistory.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <History size={16} />
                    Historique des professeurs
                  </div>
                  <div className="space-y-2">
                    {_professorHistory.map((history: ProfessorHistory, idx: number) => (
                      <div key={idx} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                        <span>{history.professorName}</span>
                        <span className="text-xs text-gray-400">{history.changedAt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === 'reschedule' && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <RefreshCcw className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Report de séance
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      L'ancienne séance sera annulée et une nouvelle sera créée à la date choisie.
                    </p>
                  </div>
                </div>
              </div>

              {/* Show selected session info (read-only) */}
              {selectedSessionId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Séance à reporter
                  </label>
                  <div className="px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {(() => {
                        const session = sessions.find(s => s.id === selectedSessionId);
                        if (!session) return 'Séance non trouvée';
                        return new Date(session.date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        });
                      })()}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {entry.startTime} - {entry.endTime}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvelle date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heure de début
                  </label>
                  <input
                    type="time"
                    value={rescheduleStartTime}
                    onChange={(e) => {
                      setRescheduleStartTime(e.target.value);
                      setRescheduleEndTime(updateEndTime(e.target.value, entry.sessionTypeId));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    value={rescheduleEndTime}
                    onChange={(e) => setRescheduleEndTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison du report <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Ex: Rattrapage suite à absence..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                />
              </div>
            </div>
          )}

          {mode === 'change_timing' && (
            <div className="space-y-4">
              {/* Scope selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Portée de la modification
                </label>
                <div className="space-y-2">
                  <button
                    onClick={() => setTimingChangeScope('single')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      timingChangeScope === 'single'
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        timingChangeScope === 'single' ? 'border-blue-500' : 'border-gray-300'
                      }`}>
                        {timingChangeScope === 'single' && (
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Cette séance uniquement</p>
                        <p className="text-xs text-gray-500">Les autres séances seront ajustées pour maintenir le volume horaire</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setTimingChangeScope('all_following')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      timingChangeScope === 'all_following'
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        timingChangeScope === 'all_following' ? 'border-blue-500' : 'border-gray-300'
                      }`}>
                        {timingChangeScope === 'all_following' && (
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Cette séance et les suivantes</p>
                        <p className="text-xs text-gray-500">Toutes les séances futures auront le nouvel horaire</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Time inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heure de début
                  </label>
                  <input
                    type="time"
                    value={newStartTime}
                    onChange={(e) => setNewStartTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    value={newEndTime}
                    onChange={(e) => setNewEndTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Day of week only for all_following mode */}
              {timingChangeScope === 'all_following' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jour de la semaine
                  </label>
                  <select
                    value={newDayOfWeek}
                    onChange={(e) => setNewDayOfWeek(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {[1, 2, 3, 4, 5, 6].map(day => (
                      <option key={day} value={day}>{dayLabels[day]}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Preview of adjustments */}
              {isLoadingPreview ? (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Calcul des ajustements...</span>
                  </div>
                </div>
              ) : timingPreview && timingPreview.adjustments.length > 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 mb-2">
                        Ajustements prévus pour maintenir le volume horaire ({timingPreview.volumeHoraire}h)
                      </p>
                      <div className="space-y-1">
                        {timingPreview.adjustments.map((adj, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-amber-700">
                            <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              adj.type === 'deleted' ? 'bg-red-500' :
                              adj.type === 'added' ? 'bg-green-500' :
                              adj.type === 'shortened' ? 'bg-orange-500' :
                              adj.type === 'extended' ? 'bg-blue-500' :
                              'bg-gray-500'
                            }`} />
                            <span>
                              <strong>{adj.sessionDate}:</strong> {adj.details}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : timingPreview && timingPreview.durationDiff === 0 ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-700">
                      Aucun ajustement nécessaire - la durée reste identique
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {mode === 'change_professor' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">
                      Changement de professeur
                    </p>
                    <p className="text-sm text-purple-700 mt-1">
                      Le changement s'appliquera à toutes les séances futures. L'historique sera conservé.
                    </p>
                  </div>
                </div>
              </div>

              {/* Current professor */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">
                    {currentProfessor?.firstName?.[0]}{currentProfessor?.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {currentProfessor ? `${currentProfessor.firstName} ${currentProfessor.lastName}` : 'Non assigné'}
                  </p>
                  <p className="text-xs text-gray-500">Professeur actuel</p>
                </div>
              </div>

              {/* New professor selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau professeur
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {professors
                    .filter(p => p.id !== entry.professorId)
                    .map(professor => (
                    <button
                      key={professor.id}
                      onClick={() => setNewProfessorId(professor.id)}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        newProfessorId === professor.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-700">
                          {professor.firstName?.[0]}{professor.lastName?.[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {professor.firstName} {professor.lastName}
                        </p>
                        {professor.staffCode && (
                          <p className="text-xs text-gray-500">{professor.staffCode}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {newProfessorId !== entry.professorId && newProfessor && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-purple-700">
                    Nouveau: <strong>{newProfessor.firstName} {newProfessor.lastName}</strong>
                  </span>
                </div>
              )}
            </div>
          )}

          {mode === 'change_session_type' && (
            <div className="space-y-4">
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Tag className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-teal-800">
                      Changement de type de séance
                    </p>
                    <p className="text-sm text-teal-700 mt-1">
                      Les séances précédentes conserveront leur type d'origine. Les séances à partir de celle-ci seront régénérées avec le nouveau type et la nouvelle durée.
                    </p>
                  </div>
                </div>
              </div>

              {/* Session type selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de séance
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {sessionTypes.map((st) => (
                    <button
                      key={st.id}
                      onClick={() => setNewSessionTypeId(st.id)}
                      className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                        newSessionTypeId === st.id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div
                        className="absolute top-3 right-3 w-3 h-3 rounded-full"
                        style={{ backgroundColor: st.color || '#6366F1' }}
                      />
                      <p className="font-medium text-gray-900 text-sm">{st.name}</p>
                      <p className="text-xs text-gray-500">{formatDuration(st.durationMinutes)}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === 'change_room' && (
            <div className="space-y-4">
              <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <DoorOpen className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-cyan-800">
                      Changement de salle
                    </p>
                    <p className="text-sm text-cyan-700 mt-1">
                      Choisissez la portée du changement et sélectionnez la nouvelle salle.
                    </p>
                  </div>
                </div>
              </div>

              {/* Scope selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Appliquer le changement à
                </label>
                <div className="space-y-2">
                  <button
                    onClick={() => setRoomChangeScope('current')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      roomChangeScope === 'current'
                        ? 'border-cyan-400 bg-cyan-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        roomChangeScope === 'current' ? 'border-cyan-500' : 'border-gray-300'
                      }`}>
                        {roomChangeScope === 'current' && (
                          <div className="w-2 h-2 rounded-full bg-cyan-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Cette séance uniquement</p>
                        <p className="text-xs text-gray-500">Change la salle seulement pour la séance sélectionnée</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setRoomChangeScope('all_following')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      roomChangeScope === 'all_following'
                        ? 'border-cyan-400 bg-cyan-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        roomChangeScope === 'all_following' ? 'border-cyan-500' : 'border-gray-300'
                      }`}>
                        {roomChangeScope === 'all_following' && (
                          <div className="w-2 h-2 rounded-full bg-cyan-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Toutes les séances restantes</p>
                        <p className="text-xs text-gray-500">Change la salle pour cette séance et toutes les futures</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Room selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvelle salle
                </label>
                {/* Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher une salle..."
                    value={roomSearchQuery}
                    onChange={(e) => setRoomSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {/* Option to clear room */}
                  <button
                    onClick={() => setNewRoomId('')}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                      newRoomId === '' ? 'bg-cyan-50' : ''
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <DoorOpen size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm text-gray-500 italic">Sans salle assignée</span>
                    {newRoomId === '' && (
                      <Check size={16} className="text-cyan-600 ml-auto" />
                    )}
                  </button>
                  {rooms
                    .filter(room => {
                      if (!roomSearchQuery) return true;
                      const query = roomSearchQuery.toLowerCase();
                      return room.name.toLowerCase().includes(query) ||
                        room.fullName.toLowerCase().includes(query) ||
                        room.floorName.toLowerCase().includes(query);
                    })
                    .map((room) => (
                    <button
                      key={room.id}
                      onClick={() => setNewRoomId(room.id)}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        newRoomId === room.id ? 'bg-cyan-50' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <DoorOpen size={14} className="text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {room.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {room.floorName}
                          {room.capacity && ` • ${room.capacity} places`}
                        </p>
                      </div>
                      {newRoomId === room.id && (
                        <Check size={16} className="text-cyan-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === 'unshare' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200 text-sm text-indigo-900 space-y-2">
                <p className="font-semibold">Ce cours redevient indépendant</p>
                <p>
                  Il ne sera plus lié à{' '}
                  {(entry.sharedWith ?? []).map((sw) => `${sw.groupeName} (${sw.moduleElementName})`).join(', ')}.
                  Ses séances restent au même créneau et dans la même salle : déplacez ensuite l'un des deux cours,
                  sinon ils restent en conflit (même professeur, même heure).
                </p>
                <p className="text-xs text-indigo-700">
                  Aucune présence n'est supprimée. Le professeur sera de nouveau rémunéré pour chaque cours séparément.
                </p>
              </div>
            </div>
          )}

          {mode === 'delete' && (
            <div className="space-y-4">
              {/* Delete scope selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Que souhaitez-vous supprimer ?
                </label>
                <div className="space-y-2">
                  <button
                    onClick={() => setDeleteScope('current')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      deleteScope === 'current'
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        deleteScope === 'current' ? 'border-red-500' : 'border-gray-300'
                      }`}>
                        {deleteScope === 'current' && (
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Cette séance uniquement</p>
                        <p className="text-xs text-gray-500">Supprime seulement la séance sélectionnée</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setDeleteScope('all_following')}
                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                      deleteScope === 'all_following'
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        deleteScope === 'all_following' ? 'border-red-500' : 'border-gray-300'
                      }`}>
                        {deleteScope === 'all_following' && (
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Cette séance et toutes les suivantes</p>
                        <p className="text-xs text-gray-500">Les séances passées sont conservées (historique, présences)</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Selected session: the one to delete, or the first of the range */}
              {selectedDeleteSessionId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {deleteScope === 'current' ? 'Séance à supprimer' : 'À partir de la séance du'}
                  </label>
                  {(() => {
                    const selectedSession = sessions.find(s => s.id === selectedDeleteSessionId);
                    if (!selectedSession) return null;
                    return (
                      <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(selectedSession.date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {selectedSession.startTime} - {selectedSession.endTime}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Warning */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">
                      {deleteScope === 'current' ? 'Suppression d\'une séance' : 'Suppression des séances à venir'}
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      {deleteScope === 'current'
                        ? 'Cette action supprimera uniquement la séance sélectionnée. Cette action est irréversible.'
                        : 'Cette action supprimera la séance sélectionnée et toutes celles qui la suivent. Les séances déjà passées restent dans l\'historique. Cette action est irréversible.'}
                    </p>
                    <p className="text-xs text-red-700 mt-2">
                      Une séance dont la présence a déjà été saisie est protégée : seuls le directeur
                      pédagogique, le directeur général ou le branch manager peuvent la supprimer, après confirmation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Attendance protection: the server refused, with the sessions concerned */}
              {attendanceWarning && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">
                        {attendanceWarning.canForce
                          ? 'Des présences ont déjà été saisies'
                          : 'Suppression refusée'}
                      </p>
                      <p className="text-sm text-amber-800 mt-1">{attendanceWarning.message}</p>
                      {attendanceWarning.sessions.length > 0 && (
                        <ul className="mt-2 text-xs text-amber-900 space-y-0.5">
                          {attendanceWarning.sessions.map((x) => (
                            <li key={x.id}>
                              • {new Date(x.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}{' '}
                              {x.startTime}-{x.endTime}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {attendanceWarning.canForce && (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setAttendanceWarning(null)}
                        className="px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 rounded-lg"
                      >
                        Garder ces séances
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(true)}
                        disabled={isSubmitting}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
                      >
                        Supprimer quand même, les présences seront à refaire
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {mode !== 'view' && (
            <button
              onClick={() => setMode('view')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Retour
            </button>
          )}
          {mode === 'view' && <div />}

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Fermer
            </button>

            {mode === 'reschedule' && (
              <button
                onClick={handleReschedule}
                disabled={isSubmitting || !rescheduleDate || !cancelReason.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                Reporter la séance
              </button>
            )}

            {mode === 'change_timing' && (
              <button
                onClick={handleChangeTiming}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                Modifier l'horaire
              </button>
            )}

            {mode === 'change_professor' && (
              <button
                onClick={handleChangeProfessor}
                disabled={isSubmitting || newProfessorId === entry.professorId}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />}
                Confirmer le changement
              </button>
            )}

            {mode === 'change_session_type' && (
              <button
                onClick={handleChangeSessionType}
                disabled={isSubmitting || newSessionTypeId === entry.sessionTypeId}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                Modifier le type
              </button>
            )}

            {mode === 'change_room' && (
              <button
                onClick={handleChangeRoom}
                disabled={isSubmitting || newRoomId === (entry.roomId || '')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <DoorOpen className="w-4 h-4" />}
                {roomChangeScope === 'current' ? 'Modifier cette séance' : 'Modifier toutes'}
              </button>
            )}

            {mode === 'unshare' && (
              <button
                onClick={handleUnshare}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                Oui, séparer les groupes
              </button>
            )}

            {mode === 'delete' && (
              <button
                onClick={() => handleDeleteEntry(false)}
                disabled={
                  isSubmitting ||
                  attendanceWarning !== null ||
                  (!selectedDeleteSessionId && !(deleteScope === 'all_following' && preselectedSessionDate))
                }
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleteScope === 'current' ? 'Supprimer cette séance' : 'Supprimer à partir de cette séance'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSessionModal;
